import { FC, useState } from "react";

import { Stack } from "@mui/material";
import { hexColorValidationRegex } from "@simplyk/common";

import { LiveEditorColorButton } from "../LiveEditorColorButton/LiveEditorColorButton";
import { findLiveEditorPresetColor, LIVE_EDITOR_BLACK_COLOR, LIVE_EDITOR_PRESET_COLORS } from "../liveEditorColors";

import { CustomColorTile } from "./CustomColorTile/CustomColorTile";
import { InlineColorPicker } from "./InlineColorPicker/InlineColorPicker";

import { useTranslate } from "@/common/hooks/useTranslate";
import { LiveEditorBlock } from "@/components/LiveEditorElements/LiveEditorBlock/LiveEditorBlock";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";

const DEFAULT_CUSTOM_COLOR = "#0ACE88";

interface LiveEditorCustomColorCardProps {
  formColor: string;
  setFormColor: (color: string) => void;
  setColorToShowInPreview: (color: string) => void;
}

export const LiveEditorCustomColorCard: FC<LiveEditorCustomColorCardProps> = ({
  formColor,
  setFormColor,
  setColorToShowInPreview,
}) => {
  const { t } = useTranslate();
  const isFormColorHex = hexColorValidationRegex.test(formColor);
  const [pickerColor, setPickerColor] = useState(isFormColorHex ? formColor : DEFAULT_CUSTOM_COLOR);

  const [isCustomColorSelected, setIsCustomColorSelected] = useState(
    isFormColorHex && !findLiveEditorPresetColor(formColor)
  );
  const [isOpenedFromTile, setIsOpenedFromTile] = useState(false);
  const [lastCustomColor, setLastCustomColor] = useState(
    isFormColorHex && !findLiveEditorPresetColor(formColor) ? formColor : undefined
  );
  const selectedPresetColor = isCustomColorSelected ? undefined : findLiveEditorPresetColor(formColor);

  const applyColor = (color: string) => {
    setPickerColor(color);
    if (color === formColor) {
      setColorToShowInPreview(formColor);
      return;
    }
    setFormColor(color);
  };

  const selectPresetColor = (presetColor: string) => {
    setIsCustomColorSelected(false);
    applyColor(presetColor);
  };

  const commitPickerColor = (color: string) => {
    const matchingPresetColor = findLiveEditorPresetColor(color);
    if (matchingPresetColor) {
      selectPresetColor(matchingPresetColor);
      return;
    }
    const customColor = color.toUpperCase();
    setIsCustomColorSelected(true);
    setLastCustomColor(customColor);
    applyColor(customColor);
  };

  const previewColor = (color: string) => {
    setPickerColor(color);
    setColorToShowInPreview(color);
  };

  const handleCustomTileClick = () => {
    setIsCustomColorSelected(true);
    setIsOpenedFromTile(true);
    applyColor(lastCustomColor ?? pickerColor);
  };

  return (
    <LiveEditorBlock>
      <Stack gap={1.5}>
        <Stack direction="row" gap={0.5} alignItems="baseline" flexWrap="wrap">
          <DashboardTypography variant="subtitle1">
            {t("dashboard", "liveFormEditor.stepColor.selectColor")}
          </DashboardTypography>
          <DashboardTypography variant="body2" vibe="text-neutral-quiet">
            {t("dashboard", "liveFormEditor.stepColor.orAddYourOwn")}
          </DashboardTypography>
        </Stack>

        <Stack direction="row" gap={1.5} alignItems="flex-start" flexWrap="wrap">
          {LIVE_EDITOR_PRESET_COLORS.map((color) => (
            <LiveEditorColorButton
              key={color}
              color={color}
              isSelected={selectedPresetColor === color}
              onClick={() => selectPresetColor(color)}
            />
          ))}

          <LiveEditorColorButton
            isDiagonal
            isSelected={selectedPresetColor === LIVE_EDITOR_BLACK_COLOR}
            onClick={() => selectPresetColor(LIVE_EDITOR_BLACK_COLOR)}
          />

          <CustomColorTile
            customColor={lastCustomColor}
            isSelected={isCustomColorSelected}
            onClick={handleCustomTileClick}
          />
        </Stack>

        {isCustomColorSelected ? (
          <InlineColorPicker
            color={pickerColor}
            onPreview={previewColor}
            onCommit={commitPickerColor}
            shouldFocusHexInput={isOpenedFromTile}
          />
        ) : null}
      </Stack>
    </LiveEditorBlock>
  );
};
